import { InjectionToken } from '@angular/core';

/**
 * Configuration interface for Analytics Logging Library
 */
export interface AnalyticsLoggingConfig {
  /**
   * Base URL for the Analytics API
   * @example 'http://localhost:3000'
   * @example 'https://api.yourcompany.com'
   */
  apiBaseUrl: string;

  /**
   * Optional: Enable debug logging
   * @default false
   */
  debug?: boolean;

  /**
   * Optional: Enable automatic error logging
   * When true, automatically logs client-side errors
   * @default true
   */
  autoLogErrors?: boolean;

  /**
   * Optional: Enable performance monitoring
   * When true, logs slow API calls and performance metrics
   * @default false
   */
  enablePerformanceMonitoring?: boolean;

  /**
   * Optional: Performance threshold in milliseconds
   * API calls slower than this will be logged
   * @default 3000
   */
  performanceThreshold?: number;

  /**
   * Optional: Enable client event batching
   * When true, batches multiple events before sending
   * @default false
   */
  enableBatching?: boolean;

  /**
   * Optional: Batch interval in milliseconds
   * How long to wait before sending batched events
   * @default 30000 (30 seconds)
   */
  batchInterval?: number;
}

/**
 * Injection token for Analytics Logging API configuration
 * 
 * @usage
 * ```typescript
 * // In app.config.ts or module providers
 * providers: [
 *   {
 *     provide: ANALYTICS_LOGGING_API_CONFIG,
 *     useValue: {
 *       apiBaseUrl: environment.apiUrl
 *     }
 *   }
 * ]
 * ```
 */
export const ANALYTICS_LOGGING_API_CONFIG = new InjectionToken<AnalyticsLoggingConfig>(
  'ANALYTICS_LOGGING_API_CONFIG',
  {
    providedIn: 'root',
    factory: () => {
      // Default configuration - should be overridden by consuming app
      console.warn(
        'ANALYTICS_LOGGING_API_CONFIG not provided. Using default configuration. ' +
        'Please provide configuration using provideAnalyticsLoggingLib() or by providing ANALYTICS_LOGGING_API_CONFIG token.'
      );
      return {
        apiBaseUrl: 'http://localhost:3000',
        debug: false,
        autoLogErrors: true,
        enablePerformanceMonitoring: false,
        performanceThreshold: 3000,
        enableBatching: false,
        batchInterval: 30000
      };
    }
  }
);
